/**
 * What outlives a seizoen: the functies, klassen, niveaus, standaard speelschema's and the
 * appointments. Every seizoen draws on the same lists.
 */
export const stamgegevens = {
  scope: {
    wedstrijdbaan: "Wedstrijdbaan",
    wedstrijddag: "Wedstrijddag",
  },

  /** Whether a functie must be filled. Only the stagefuncties are optioneel (ADR-0061). */
  noodzaak: {
    verplicht: "Verplicht",
    optioneel: "Optioneel",
  },

  leeftijdsgroep: {
    junioren: "Junioren",
    aspiranten: "Aspiranten",
    jarenJunioren: "12 t/m 15 jaar",
    jarenAspiranten: "16 t/m 18 jaar",
    /** The name with its years, for a line that has no room for a second one. */
    metJarenJunioren: "Junioren (12-15 jaar)",
    metJarenAspiranten: "Aspiranten (16-18 jaar)",
    scheiding: " en ",
  },

  functies: {
    lijstLeeg: "Er zijn nog geen functies.",
    terug: "Alle functies",
    kies: "Kies een functie om die te wijzigen of in te stellen waar die nodig is.",
    toevoegen: "Functie toevoegen",
    toevoegenUitleg:
      "Waar de functie nodig is, stel je daarna in bij de functie zelf.",
    submitToevoegen: "Toevoegen",
    naam: "Naam",
    naamPlaceholder: "Bijvoorbeeld Baancommissaris",
    naamUitleg:
      "Zoals JBNL de functie in het wedstrijdboekje schrijft. Heeft een functie twee mensen nodig? Maak er dan twee functies van, elk met een eigen naam.",
    weergavenaam: "Weergavenaam",
    weergavenaamPlaceholder: "Optioneel, bijvoorbeeld Pompbediener",
    weergavenaamUitleg:
      "Vul je een weergavenaam in? Dan drukt het wedstrijdboekje die af in plaats van de naam hierboven. Geef je twee functies dezelfde weergavenaam? Dan komen ze op één regel in het wedstrijdboekje te staan.",
    dient: "Geldt voor",
    dientBaan: "Eén wedstrijdbaan",
    dientDag: "De hele wedstrijddag",
    scopeVast: "Staat vast zolang de functie in een bezetting voorkomt.",
    moetBezetZijn: "Moet bezet zijn",
    /* Unlocked, unlike Dient: making a functie verplicht is always allowed. */
    moetBezetZijnUitleg:
      "Een verplichte functie die niemand vervult, komt in de planning als waarschuwing te staan. Een optionele functie mag leeg blijven en deel je zelf in.",
    /* Shown only on an optionele functie: on every row the word "Verplicht" would say nothing. */
    scopeMetNoodzaak: "{{scope}} · {{noodzaak}}",
    weergavenaamInLijst: " · drukt af als {{weergavenaam}}",
    /* Not "nodig": an optionele functie is needed nowhere and still appears here. */
    bezettingTitle: "Waar kan deze functie staan?",
    verwijderenTitle: "Functie verwijderen",
    verwijderenUitleg:
      "Verwijderen kan zolang de functie in geen bezetting staat, niemand ervoor bevoegd is en geen planning de functie indeelt.",
    verwijderenVraag: "{{naam}} verwijderen?",
    verwijderenDescription: "Je kunt dit niet ongedaan maken.",
    verwijderenConfirm: "{{naam}} verwijderen",
  },

  /** ADR-0021 keys the matrix on klasse *and* niveau. Ticking a box does not save. */
  bezetting: {
    uitlegBaan:
      "Vink aan bij welke klasse en welk niveau een wedstrijdbaan deze functie nodig heeft.",
    uitlegDag:
      "Deze functie geldt voor de hele wedstrijddag. Vink aan op welke niveaus een wedstrijddag die nodig heeft.",
    caption: "Bezetting voor {{functie}}",
    klasse: "Klasse",
    heleDag: "Hele wedstrijddag",
    vinkBaan: "{{functie}} bij {{klasse}}, {{niveau}}",
    vinkDag: "{{functie}} op {{niveau}}",
    vuil: "Niet opgeslagen wijzigingen.",
    opslaan: "Bezetting opslaan",
    nergens:
      "Nergens nodig. Een klasse en niveau zonder bezetting geldt als <em>nog niet ingesteld</em>, en niet als <em>geen bezetting nodig</em>. De planning waarschuwt erover zodra er een baan voor bestaat.",
  },

  klassen: {
    toevoegen: "Klasse toevoegen",
    wijzigen: "Wijzigen",
    wijzigTitle: "{{naam}} wijzigen",
    verwijderen: "Verwijderen",
    verwijderVraag: "{{naam}} verwijderen?",
    verwijderDescription: "Je kunt dit niet ongedaan maken.",
    verwijderConfirm: "{{naam}} verwijderen",
    submitToevoegen: "Toevoegen",
    naam: "Naam",
    openVoor: "Staat open voor",
    openVoorUitleg:
      "Een klasse die voor beide openstaat, zegt niets over de leeftijd van een ploeg.",
  },

  niveaus: {
    toevoegen: "Niveau toevoegen",
    wijzigen: "Wijzigen",
    wijzigTitle: "{{naam}} wijzigen",
    verwijderen: "Verwijderen",
    verwijderVraag: "{{naam}} verwijderen?",
    verwijderDescription: "Je kunt dit niet ongedaan maken.",
    verwijderConfirm: "{{naam}} verwijderen",
    submitToevoegen: "Toevoegen",
    naam: "Naam",
    naamUitleg:
      "De regio's organiseren de lokale wedstrijden zelf. Die komen hier niet voor.",
    /** Said out loud: ADR-0021 rejected a staffing rule keyed on this order. */
    volgordeUitleg:
      "De volgorde bepaalt alleen hoe de lijst eruitziet. De bezetting staat per klasse en niveau bij de functie zelf. De speelschema's staan bij Speelschema's.",
  },

  speelschemas: {
    terug: "Alle niveaus",
    hernoemen: "Een niveau hernoemen of verplaatsen doe je bij Niveaus.",
    kies: "Kies een niveau om te zien met welke tijden een wedstrijdbaan er begint.",
    kaartTitel: "{{klasse}} op {{niveau}}",
    uitleg:
      "Laat een vakje leeg om er een ploeg in te kunnen delen. Vul je een vakje in, bijvoorbeeld met een <em>Pauze</em>? Dan gebeurt dat op die tijd. Er kan dan geen ploeg meer bij.",
    leeg: "Geen standaardspeelschema. Een nieuwe baan van deze klasse op dit niveau begint leeg. Een lege baan klopt voor een combinatie die JBNL niet speelt, zoals Brandbestrijding op de halve finale.",
    tijdVeld: "Tijd {{nummer}} voor {{klasse}} op {{niveau}}",
    labelVeld: "Label {{nummer}} voor {{klasse}} op {{niveau}}",
    vrij: "Vrij voor een ploeg",
    regelWeghalen: "Regel {{nummer}} weghalen",
    regelToevoegen: "Regel toevoegen",
    plekken_one: "{{count}} plek voor een ploeg.",
    plekken_other: "{{count}} plekken voor een ploeg.",
    vuil: "Niet opgeslagen wijzigingen.",
    /** The klasse is in the label: four of these buttons sit on one niveau, one per klasse. */
    opslaan: "Speelschema voor {{klasse}} opslaan",
    strip: "Klassen op dit niveau",
  },

  bevoegdheden: {
    /** The reverse lookup: pick a functie, see who may stand there. */
    filterFunctie: "Functie",
    terug: "Andere persoon",
    kies: "Zoek een persoon. Je ziet en wijzigt dan voor welke functies die persoon bevoegd is.",
    title: "Bevoegd voor",
    uitleg:
      "Een bevoegdheid blijft gelden na het einde van een seizoen. Of iemand dit seizoen meedoet, lees je bij de beschikbaarheid en niet in deze lijst.",
    geenFuncties:
      "Er zijn nog geen functies waarvoor je iemand bevoegd kunt maken.",
  },

  errors: {
    functies: "Kon de functies niet ophalen.",
    klassen: "Kon de klassen niet ophalen.",
    niveaus: "Kon de niveaus niet ophalen.",
    bezetting: "Kon de bezetting niet ophalen.",
    functieToevoegen: "Kon de functie niet toevoegen.",
    functieOpslaan: "Kon de functie niet opslaan.",
    functieVerwijderen: "Kon de functie niet verwijderen.",
    bezettingOpslaan: "Kon de bezetting niet opslaan.",
    klasseToevoegen: "Kon de klasse niet toevoegen.",
    klasseOpslaan: "Kon de klasse niet opslaan.",
    klasseVerwijderen: "Kon de klasse niet verwijderen.",
    niveauToevoegen: "Kon het niveau niet toevoegen.",
    niveauOpslaan: "Kon het niveau niet opslaan.",
    niveauVerwijderen: "Kon het niveau niet verwijderen.",
    bevoegdhedenOphalen: "Kon de bevoegdheden niet ophalen.",
    bevoegdheidOpslaan: "Kon de bevoegdheid niet opslaan.",
    speelschemas: "Kon de speelschema's niet ophalen.",
    opslaan: "Opslaan is mislukt.",
    volgorde: "Kon de volgorde niet opslaan.",
  },
} as const;
